'use client';

import { c as _c } from "react/compiler-runtime";
import type { Address } from 'viem';
import { type ChainId, ChainUtils } from '@morpho-org/blue-sdk';
import { ClipboardButton } from '@morpho-org/ui-v6/client';
import { cn, isZeroAddressOrUnset } from '@morpho-org/ui-v6/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { ArrowDiagonalUp } from '@repo/ui/icons';
import { AbbreviatedHex } from '@repo/web3';
import { memo, useMemo } from 'react';
import { ButtonLink } from '@/components/common/ButtonLink';
export interface TextAddressProps {
  address: Address;
  toastMessage?: string;
  tooltipMessage?: string;
  targetChainId: ChainId;
  explorerName?: string;
  /** text-* class controlling the text variant (default: `text-md`). */
  textClassName?: string;
  /** text-* class controlling the text color (default: `text-foreground`). */
  textColorClass?: string;
  noLink?: boolean;
}

/**
 * Component to display an address with a copy button and explorer redirection
 *
 * @param address - The address to display, redirect & copy
 * @param toastMessage - The message to display when the address is copied to clipboard
 * @param tooltipMessage - The message to display in the tooltip when the address is copied to clipboard
 * @param targetChainId - The chain id of the target chain
 * @param explorerName - The name of the explorer to use
 * @param textClassName - The text-* class of the text to use
 * @param noLink - Whether to hide the button link to the explorer
 */

const TextAddress = t0 => {
  const $ = _c(23);
  const {
    address,
    toastMessage,
    tooltipMessage,
    targetChainId,
    explorerName,
    textClassName: t1,
    textColorClass: t2,
    noLink: t3
  } = t0;
  const textClassName = t1 === undefined ? "text-md" : t1;
  const textColorClass = t2 === undefined ? "text-foreground" : t2;
  const noLink = t3 === undefined ? false : t3;
  let t4;
  if ($[0] !== address || $[1] !== targetChainId) {
    t4 = ChainUtils.getExplorerAddressUrl(targetChainId, address);
    $[0] = address;
    $[1] = targetChainId;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const addressLink = t4;
  let t5;
  if ($[3] !== textClassName || $[4] !== textColorClass) {
    t5 = cn(textClassName, textColorClass);
    $[3] = textClassName;
    $[4] = textColorClass;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  let t6;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      lineHeight: "normal"
    };
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  let t7;
  if ($[7] !== address || $[8] !== t5) {
    t7 = <AbbreviatedHex value={address} className={t5} style={t6} />;
    $[7] = address;
    $[8] = t5;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  const t8 = toastMessage || "Address copied to clipboard.";
  let t9;
  if ($[10] !== address || $[11] !== t8 || $[12] !== tooltipMessage) {
    t9 = <ClipboardButton size="sm" toCopy={address} toastMessage={t8} tooltipMessage={tooltipMessage} />;
    $[10] = address;
    $[11] = t8;
    $[12] = tooltipMessage;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== address || $[15] !== addressLink || $[16] !== explorerName || $[17] !== noLink) {
    t10 = !isZeroAddressOrUnset(address) && <div className="flex items-center">{!noLink && <Tooltip><TooltipTrigger asChild={true}><div className="flex"><ButtonLink href={addressLink} buttonProps={{
              variant: "ghost",
              size: "sm",
              iconRight: undefined,
              iconAlone: <ArrowDiagonalUp />,
              iconAloneProps: {
                className: "text-secondary-foreground"
              }
            }} /></div></TooltipTrigger><TooltipContent>{`Go to ${explorerName || "block explorer"}.`}</TooltipContent></Tooltip>}</div>;
    $[14] = address;
    $[15] = addressLink;
    $[16] = explorerName;
    $[17] = noLink;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] !== t10 || $[20] !== t7 || $[21] !== t9) {
    t11 = <div className="gap-2xs flex h-[25.6px] items-center">{t7}{t9}{t10}</div>;
    $[19] = t10;
    $[20] = t7;
    $[21] = t9;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  return t11;
};
export default memo(TextAddress);